import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { useTickets } from '@/lib/adminApi';
import type { TicketSummary } from '@ventin/shared';
import { PriorityBadge, CategoryBadge } from '@/components/tickets/ticketMeta';

/**
 * MyTicketsPage — /portal/tickets (TKT-07 client side).
 *
 * Renders a table of the caller's company tickets via useTickets() with
 * NO companyId override — the API enforces scoping at the service layer.
 * Read-only: no filters, no status toggle, no billable controls.
 */
export default function MyTicketsPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useTickets();
  const tickets = useMemo(() => data?.tickets ?? [], [data]);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading tickets…</p>;
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Couldn't load tickets — try again.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div>
      <header className="mb-6">
        <h2 className="text-2xl font-semibold leading-[1.3]">My Tickets</h2>
        <p className="text-sm text-muted-foreground mt-1">
          All support tickets submitted by your organisation.
        </p>
      </header>

      {data && (
        <div className="rounded-md border bg-background overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Priority</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    <p>You haven&apos;t submitted any tickets yet.</p>
                    <Link
                      to="/portal/tickets/new"
                      className="mt-2 inline-block text-primary underline underline-offset-4 hover:opacity-80"
                    >
                      Submit your first ticket
                    </Link>
                  </td>
                </tr>
              )}
              {tickets.map((t) => (
                <tr key={t.id} className="border-t">
                  <td className="px-4 py-3 font-medium">{t.title}</td>
                  <td className="px-4 py-3">
                    <TicketStatusChip status={t.status} />
                  </td>
                  <td className="px-4 py-3">
                    <PriorityBadge priority={t.priority} />
                  </td>
                  <td className="px-4 py-3">
                    <CategoryBadge category={t.category} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(t.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/portal/tickets/${t.id}`)}
                      aria-label={`View ${t.title}`}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TicketStatusChip (local copy — clients don't need the interactive admin chip)
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<TicketSummary['status'], { bg: string; dot: string; label: string }> = {
  OPEN: { bg: 'bg-blue-50 text-blue-700', dot: 'bg-blue-500', label: 'Open' },
  IN_PROGRESS: { bg: 'bg-yellow-50 text-yellow-700', dot: 'bg-yellow-500', label: 'In Progress' },
  WAITING_CLIENT: { bg: 'bg-orange-50 text-orange-700', dot: 'bg-orange-400', label: 'Waiting' },
  RESOLVED: { bg: 'bg-green-50 text-green-700', dot: 'bg-green-500', label: 'Resolved' },
  CLOSED: { bg: 'bg-[#F1F1F3] text-[#4B4B52]', dot: 'bg-[#8A8A94]', label: 'Closed' },
};

function TicketStatusChip({ status }: { status: TicketSummary['status'] }) {
  const s = STATUS_STYLES[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-[10px] py-1 text-[12px] font-medium',
        s.bg,
      )}
    >
      <span aria-hidden="true" className={cn('inline-block h-[6px] w-[6px] rounded-full', s.dot)} />
      {s.label}
    </span>
  );
}
